<ng-container *vsLet="canView$ | async as canView">
  <ng-container *ngIf="canView">
    <vs-card *ngIf="ledger$ | async as ledger" class="w-full">
      <vs-card-header
        [config]="{
          title: 'Contract Ledger',
          subtitle:
            'Components, allocations, funding, and disbursements recorded against this contract, for reconciliation and audit.',
        }"
      ></vs-card-header>

      <div
        *ngIf="!ledger.hasLedgerData"
        class="flex items-start gap-2 mx-4 mb-4 rounded-xl bg-gray-50 border border-gray-200 p-3 text-sm text-gray-800"
      >
        <vs-icon name="info-circle" size="16" class="mt-0.5 shrink-0"></vs-icon>
        <div>
          <p class="font-semibold">No ledger data recorded for this contract</p>
          <p class="mt-1">
            This contract predates ledger tracking (no historical activity is backfilled) or has no priced components.
            The balances below are not a reconciled answer — look up this contract's payment history directly instead.
          </p>
        </div>
      </div>

      <div
        *ngIf="ledger.unreconciledSourceEvents.length"
        class="flex items-start gap-2 mx-4 mb-4 rounded-xl bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-800"
      >
        <vs-icon name="alert-triangle" size="16" class="mt-0.5 shrink-0"></vs-icon>
        <div>
          <p class="font-semibold">
            {{ ledger.unreconciledSourceEvents.length }} payment{{
              ledger.unreconciledSourceEvents.length === 1 ? '' : 's'
            }}
            not reflected in this ledger
          </p>
          <ul class="mt-1 list-disc list-inside">
            <li *ngFor="let event of ledger.unreconciledSourceEvents">
              {{ event.kind === 'funding' ? 'Payment' : 'Refund' }} {{ event.source.id }} ({{
                event.amount | currency: ledger.currency : 'symbol' : '1.2-2'
              }}, {{ event.date | date: 'mediumDate' }}) has no matching {{ event.kind }} entry.
            </li>
          </ul>
        </div>
      </div>

      <div *ngIf="ledger.externalPayments?.length" class="mx-4 mb-4 rounded-xl border border-gray-200 p-4">
        <p class="font-semibold mb-2">Awaiting settlement</p>
        <div *ngFor="let payment of ledger.externalPayments" class="text-sm py-2 border-t">
          <ng-container *ngIf="payment.collectionMode === 'PAY_LATER'; else sellerInstallment">
            <p class="font-medium">
              Pay Later balance · {{ payment.status === 'FAILED' ? 'Collection failed' : 'Outstanding' }}
            </p>
            <p>Responsible payer: {{ payment.ownerName }}</p>
          </ng-container>
          <ng-template #sellerInstallment
            ><p>Seller owner: {{ payment.ownerName }}</p></ng-template
          >
          <p>
            {{ payment.amount / 100 | currency: ledger.currency }}
            <span *ngIf="payment.dueDate; else noExternalDueDate">
              · Due {{ payment.dueDate | date: 'mediumDate' : 'UTC' }}</span
            >
            <ng-template #noExternalDueDate> · No due date assigned</ng-template>
          </p>
          <a [routerLink]="['/sales', payment.id]" class="text-primary-700 underline">{{
            payment.collectionMode === 'PAY_LATER'
              ? 'View balance to collect or record receipt'
              : 'View installment and record receipt'
          }}</a>
        </div>
      </div>

      <vs-tabs theme="primary">
        <vs-tab [config]="{ label: 'Overview' }">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4">
            <div class="border rounded-xl p-4">
              <p class="text-xs font-medium text-gray-600">Contract Amount Due</p>
              <p class="text-lg font-semibold text-gray-900">
                {{ ledger.fundingBalance.contractAmountDue | currency: ledger.currency : 'symbol' : '1.2-2' }}
              </p>
              <div *ngIf="componentsByParty(ledger).length" class="mt-2 pt-2 border-t space-y-0.5">
                <p
                  *ngFor="let contribution of componentsByParty(ledger)"
                  class="text-xs text-gray-500 flex justify-between gap-2"
                >
                  <span class="truncate">{{ describeParty(contribution.party) }}</span>
                  <span class="shrink-0">{{
                    contribution.amount | currency: ledger.currency : 'symbol' : '1.2-2'
                  }}</span>
                </p>
              </div>
            </div>
            <div class="border rounded-xl p-4">
              <p class="text-xs font-medium text-gray-600">Net Funding Received</p>
              <p class="text-lg font-semibold text-gray-900">
                {{ ledger.fundingBalance.netFunding | currency: ledger.currency : 'symbol' : '1.2-2' }}
              </p>
              <div *ngIf="fundingByParty(ledger).length" class="mt-2 pt-2 border-t space-y-0.5">
                <p
                  *ngFor="let contribution of fundingByParty(ledger)"
                  class="text-xs text-gray-500 flex justify-between gap-2"
                >
                  <span class="truncate">{{ describeParty(contribution.party) }}</span>
                  <span class="shrink-0">{{
                    contribution.amount | currency: ledger.currency : 'symbol' : '1.2-2'
                  }}</span>
                </p>
              </div>
            </div>
            <div class="border rounded-xl p-4">
              <p class="text-xs font-medium text-gray-600">Remaining Funding Balance</p>
              <p class="text-lg font-semibold text-gray-900">
                {{ ledger.fundingBalance.remainingFundingBalance | currency: ledger.currency : 'symbol' : '1.2-2' }}
              </p>
              <div class="mt-2 pt-2 border-t space-y-0.5">
                <p class="text-xs text-gray-500 flex justify-between gap-2">
                  <span>Contract Amount Due</span>
                  <span class="shrink-0">{{
                    ledger.fundingBalance.contractAmountDue | currency: ledger.currency : 'symbol' : '1.2-2'
                  }}</span>
                </p>
                <p class="text-xs text-gray-500 flex justify-between gap-2">
                  <span>Net Funding Received</span>
                  <span class="shrink-0"
                    >− {{ ledger.fundingBalance.netFunding | currency: ledger.currency : 'symbol' : '1.2-2' }}</span
                  >
                </p>
              </div>
            </div>
            <div class="border rounded-xl p-4">
              <p class="text-xs font-medium text-gray-600">Customer Refund Activity</p>
              <p class="text-lg font-semibold text-gray-900">
                {{ ledger.partyBalances.customerRefundActivity | currency: ledger.currency : 'symbol' : '1.2-2' }}
              </p>
              <div *ngIf="customerRefundsByParty(ledger).length" class="mt-2 pt-2 border-t space-y-0.5">
                <p
                  *ngFor="let contribution of customerRefundsByParty(ledger)"
                  class="text-xs text-gray-500 flex justify-between gap-2"
                >
                  <span class="truncate">{{ describeParty(contribution.party) }}</span>
                  <span class="shrink-0">{{
                    contribution.amount | currency: ledger.currency : 'symbol' : '1.2-2'
                  }}</span>
                </p>
              </div>
            </div>
            <div class="border rounded-xl p-4">
              <p class="text-xs font-medium text-gray-600">Seller Clawback Receivable</p>
              <p class="text-lg font-semibold text-gray-900">
                {{ ledger.partyBalances.sellerClawbackReceivable | currency: ledger.currency : 'symbol' : '1.2-2' }}
              </p>
              <div *ngIf="sellerClawbackContributions(ledger).length" class="mt-2 pt-2 border-t space-y-0.5">
                <p
                  *ngFor="let contribution of sellerClawbackContributions(ledger)"
                  class="text-xs text-gray-500 flex justify-between gap-2"
                >
                  <span class="truncate">{{ describeParty(contribution.party) }}</span>
                  <span class="shrink-0">{{
                    contribution.amount | currency: ledger.currency : 'symbol' : '1.2-2'
                  }}</span>
                </p>
              </div>
            </div>
          </div>

          <div class="px-4 pb-4 overflow-x-auto scrollbar-thin">
            <p class="text-xs font-medium text-gray-600 mb-2">Party Balances</p>
            <table class="min-w-full border rounded-xl overflow-hidden">
              <thead>
                <tr class="bg-gray-50 text-left text-xs font-medium text-gray-600">
                  <th class="px-4 py-2 border-b">Party</th>
                  <th class="px-4 py-2 border-b text-right">Net Allocation</th>
                  <th class="px-4 py-2 border-b text-right">Net Disbursement</th>
                  <th class="px-4 py-2 border-b text-right">Outstanding Payable</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let balance of ledger.partyBalances.parties" class="text-sm text-gray-900">
                  <td class="px-4 py-2 border-b">{{ describeParty(balance.party) }}</td>
                  <td class="px-4 py-2 border-b text-right">
                    {{ balance.netAllocation | currency: ledger.currency : 'symbol' : '1.2-2' }}
                  </td>
                  <td class="px-4 py-2 border-b text-right">
                    {{ balance.netDisbursement | currency: ledger.currency : 'symbol' : '1.2-2' }}
                  </td>
                  <td class="px-4 py-2 border-b text-right">
                    {{ balance.outstandingPayable | currency: ledger.currency : 'symbol' : '1.2-2' }}
                  </td>
                </tr>
                <tr *ngIf="!ledger.partyBalances.parties.length">
                  <td class="px-4 py-6 text-sm text-gray-500" colspan="4">No party activity recorded yet.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </vs-tab>

        <vs-tab [config]="{ label: 'Components (' + ledger.components.length + ')' }">
          <div class="px-4 pb-4 overflow-x-auto scrollbar-thin">
            <table class="min-w-full border rounded-xl overflow-hidden">
              <thead>
                <tr class="bg-gray-50 text-left text-xs font-medium text-gray-600">
                  <th class="px-4 py-2 border-b">Date</th>
                  <th class="px-4 py-2 border-b">Description</th>
                  <th class="px-4 py-2 border-b">Party</th>
                  <th class="px-4 py-2 border-b text-right">Amount</th>
                  <th class="px-4 py-2 border-b">Source</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let component of ledger.components" class="text-sm text-gray-900">
                  <td class="px-4 py-2 border-b whitespace-nowrap">{{ component.created | date: 'mediumDate' }}</td>
                  <td class="px-4 py-2 border-b">
                    {{ component.description || component.componentType }}
                    <div
                      *ngIf="component.oneTime"
                      class="text-xs text-gray-500"
                      title="Charged once, with the first payment. Never part of a renewal."
                    >
                      One-time
                    </div>
                  </td>
                  <td class="px-4 py-2 border-b">
                    {{ describeParty(component.party) }}
                    <div
                      *ngIf="component.recipientSource === recipientSource.LEGACY_FALLBACK"
                      class="text-xs text-warning-600"
                      title="No recipient was configured for this rating-matrix cell at issuance; attributed to the insurer by default."
                    >
                      Recipient not configured
                    </div>
                    <div
                      *ngIf="component.recipientSource === recipientSource.SELLER_DIMENSION"
                      class="text-xs text-gray-500"
                      title="No recipient was configured, so the seller this rating-matrix cell is scoped to was used."
                    >
                      Seller from rating-matrix cell
                    </div>
                    <div
                      *ngIf="component.recipientSource === recipientSource.ONE_TIME_DEFAULT"
                      class="text-xs text-gray-500"
                      title="No recipient was configured for this one-time fee, so it is owed to the administrator by default."
                    >
                      Administrator by default
                    </div>
                  </td>
                  <td class="px-4 py-2 border-b text-right">
                    {{ component.amount | currency: component.currency : 'symbol' : '1.2-2' }}
                  </td>
                  <td class="px-4 py-2 border-b">{{ describeSource(component.source) }}</td>
                </tr>
                <tr *ngIf="!ledger.components.length">
                  <td class="px-4 py-6 text-sm text-gray-500" colspan="5">No components recorded yet.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </vs-tab>

        <vs-tab [config]="{ label: 'Allocations (' + ledger.allocations.length + ')' }">
          <div class="px-4 pb-4 overflow-x-auto scrollbar-thin">
            <table class="min-w-full border rounded-xl overflow-hidden">
              <thead>
                <tr class="bg-gray-50 text-left text-xs font-medium text-gray-600">
                  <th class="px-4 py-2 border-b">Date</th>
                  <th class="px-4 py-2 border-b">Type</th>
                  <th class="px-4 py-2 border-b">Description</th>
                  <th class="px-4 py-2 border-b">Party</th>
                  <th class="px-4 py-2 border-b text-right">Amount</th>
                  <th class="px-4 py-2 border-b">Source</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let allocation of ledger.allocations" class="text-sm text-gray-900">
                  <td class="px-4 py-2 border-b whitespace-nowrap">
                    {{ allocation.effectiveDate | date: 'mediumDate' }}
                  </td>
                  <td class="px-4 py-2 border-b">
                    <vs-badge [config]="{ displayStatusIcon: false }">{{ allocation.type | titlecase }}</vs-badge>
                  </td>
                  <td class="px-4 py-2 border-b">{{ allocation.description || '—' }}</td>
                  <td class="px-4 py-2 border-b">{{ describeParty(allocation.party) }}</td>
                  <td class="px-4 py-2 border-b text-right">
                    {{ allocation.amount | currency: allocation.currency : 'symbol' : '1.2-2' }}
                  </td>
                  <td class="px-4 py-2 border-b">{{ describeSource(allocation.source) }}</td>
                </tr>
                <tr *ngIf="!ledger.allocations.length">
                  <td class="px-4 py-6 text-sm text-gray-500" colspan="6">No allocations recorded yet.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </vs-tab>

        <vs-tab [config]="{ label: 'Funding (' + ledger.funding.length + ')' }">
          <div class="px-4 pb-4 overflow-x-auto scrollbar-thin">
            <table class="min-w-full border rounded-xl overflow-hidden">
              <thead>
                <tr class="bg-gray-50 text-left text-xs font-medium text-gray-600">
                  <th class="px-4 py-2 border-b">Date</th>
                  <th class="px-4 py-2 border-b">Description</th>
                  <th class="px-4 py-2 border-b">Party</th>
                  <th class="px-4 py-2 border-b">Payment Method</th>
                  <th class="px-4 py-2 border-b text-right">Amount</th>
                  <th class="px-4 py-2 border-b">Source</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let entry of ledger.funding" class="text-sm text-gray-900">
                  <td class="px-4 py-2 border-b whitespace-nowrap">
                    {{ entry.receivedDate | date: 'mediumDate' : (entry.externalSettlement ? 'UTC' : undefined) }}
                  </td>
                  <td class="px-4 py-2 border-b">
                    {{ entry.description || (entry.amount < 0 ? 'Payment reversal' : 'Payment received') }}
                    <div *ngIf="entry.externalSettlement as receipt" class="text-xs text-gray-600 mt-1 space-y-1">
                      <p>
                        Externally settled · {{ receipt.channel | titlecase }} · Received
                        {{ receipt.receivedDate | date: 'mediumDate' : 'UTC' }}
                      </p>
                      <p>
                        Recorded by {{ receipt.actorName }} on
                        {{ receipt.recordedAt | date: 'medium' }}
                      </p>
                      <p *ngIf="receipt.reference">Reference: {{ receipt.reference }}</p>
                      <p *ngIf="receipt.note">Note: {{ receipt.note }}</p>
                      <a
                        *ngIf="receipt.attachment"
                        [routerLink]="['/sales', entry.source.id]"
                        class="text-primary-700 underline"
                        >View proof of receipt: {{ receipt.attachment.filename }}</a
                      >
                    </div>
                  </td>
                  <td class="px-4 py-2 border-b">{{ describeParty(entry.party) }}</td>
                  <td class="px-4 py-2 border-b">{{ entry.paymentMethod || '—' }}</td>
                  <td class="px-4 py-2 border-b text-right">
                    {{ entry.amount | currency: entry.currency : 'symbol' : '1.2-2' }}
                  </td>
                  <td class="px-4 py-2 border-b">{{ describeSource(entry.source) }}</td>
                </tr>
                <tr *ngIf="!ledger.funding.length">
                  <td class="px-4 py-6 text-sm text-gray-500" colspan="6">No funding recorded yet.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </vs-tab>

        <vs-tab [config]="{ label: 'Disbursements (' + ledger.disbursements.length + ')' }">
          <div class="px-4 pb-4 overflow-x-auto scrollbar-thin">
            <table class="min-w-full border rounded-xl overflow-hidden">
              <thead>
                <tr class="bg-gray-50 text-left text-xs font-medium text-gray-600">
                  <th class="px-4 py-2 border-b">Date</th>
                  <th class="px-4 py-2 border-b">Description</th>
                  <th class="px-4 py-2 border-b">Party</th>
                  <th class="px-4 py-2 border-b">Payment Method</th>
                  <th class="px-4 py-2 border-b text-right">Amount</th>
                  <th class="px-4 py-2 border-b">Source</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let disbursement of ledger.disbursements" class="text-sm text-gray-900">
                  <td class="px-4 py-2 border-b whitespace-nowrap">{{ disbursement.paidDate | date: 'mediumDate' }}</td>
                  <td class="px-4 py-2 border-b">
                    {{ disbursement.description || (disbursement.amount < 0 ? 'Clawback' : 'Disbursement') }}
                  </td>
                  <td class="px-4 py-2 border-b">{{ describeParty(disbursement.party) }}</td>
                  <td class="px-4 py-2 border-b">{{ disbursement.paymentMethod || '—' }}</td>
                  <td class="px-4 py-2 border-b text-right">
                    {{ disbursement.amount | currency: disbursement.currency : 'symbol' : '1.2-2' }}
                  </td>
                  <td class="px-4 py-2 border-b">{{ describeSource(disbursement.source) }}</td>
                </tr>
                <tr *ngIf="!ledger.disbursements.length">
                  <td class="px-4 py-6 text-sm text-gray-500" colspan="6">No disbursements recorded yet.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </vs-tab>
      </vs-tabs>
    </vs-card>
  </ng-container>
</ng-container>
